/**
 * Rental Vehicle Detail Page
 * Epic 34: Járműnyilvántartás (ADR-027)
 */

import type { RentalVehicle } from '@/api/vehicles';
import { useBergepRentalHistory } from '@/hooks/use-rental-equipment';
import { useVehicle, useVehicleMutations } from '@/hooks/use-vehicles';
import { useTranslation } from '@kgc/i18n';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  History,
  Pencil,
  Trash2,
  Truck,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

type VehicleType = RentalVehicle['type'];
type VehicleStatus = RentalVehicle['status'];

// TYPE_LABELS moved inside component for i18n support

function isExpiringSoon(dateStr: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return date <= thirtyDaysFromNow && date > now;
}

function isExpired(dateStr: string): boolean {
  return new Date(dateStr) <= new Date();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ExpiryBadge({ date, label }: { date: string; label: string }) {
  const expired = isExpired(date);
  const expiringSoon = isExpiringSoon(date);

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p
          className={`font-medium ${
            expired
              ? 'text-red-600 dark:text-red-400'
              : expiringSoon
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-gray-900 dark:text-white'
          }`}
        >
          {formatDate(date)}
        </p>
      </div>
      {(expired || expiringSoon) && (
        <div
          className={`rounded-full p-1 ${
            expired ? 'bg-red-100 dark:bg-red-900/30' : 'bg-orange-100 dark:bg-orange-900/30'
          }`}
        >
          <AlertTriangle
            className={`h-4 w-4 ${
              expired ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'
            }`}
          />
        </div>
      )}
    </div>
  );
}

export function RentalVehicleDetailPage() {
  const { t } = useTranslation();

  const TYPE_LABELS: Record<VehicleType, string> = {
    trailer: t('vehicle.typeLabels.trailer'),
    aggregator: t('vehicle.typeLabels.aggregator'),
    platform: t('vehicle.typeLabels.platform'),
    other: t('vehicle.typeLabels.other'),
  };

  const STATUS_CONFIG: Record<
    VehicleStatus,
    { label: string; color: string; icon: typeof CheckCircle }
  > = {
    active: {
      label: t('vehicle.statusLabels.active'),
      color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      icon: CheckCircle,
    },
    rented: {
      label: t('vehicle.statusLabels.rented'),
      color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      icon: Clock,
    },
    maintenance: {
      label: t('vehicle.statusLabels.maintenance'),
      color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      icon: AlertTriangle,
    },
    inactive: {
      label: t('vehicle.statusLabels.inactive'),
      color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      icon: XCircle,
    },
  };
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { vehicle: rawVehicle, isLoading, error } = useVehicle(id);
  const { history: rentalHistoryData, isLoading: historyLoading } = useBergepRentalHistory(id);
  const { deleteVehicle } = useVehicleMutations();
  const vehicle = rawVehicle as RentalVehicle | null;

  const handleDelete = async () => {
    if (vehicle && confirm(t('vehicle.deleteConfirm', { name: vehicle.name }))) {
      try {
        await deleteVehicle(vehicle.id);
        navigate('/vehicles/rental');
      } catch {
        alert(t('vehicle.deleteFailed'));
      }
    }
  };

  if (isLoading) {
    return (
      <div className="kgc-bg flex min-h-screen items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="kgc-bg flex min-h-screen items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="kgc-bg flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {error ?? t('vehicle.vehicleNotFound')}
          </p>
          <button
            onClick={() => navigate('/vehicles/rental')}
            className="mt-4 text-primary hover:underline"
          >
            {t('vehicle.backToList')}
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = STATUS_CONFIG[vehicle.status].icon;

  return (
    <div className="kgc-bg min-h-screen p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/vehicles/rental')}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {vehicle.licensePlate}
              </h1>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CONFIG[vehicle.status].color}`}
              >
                <StatusIcon className="h-3 w-3" />
                {STATUS_CONFIG[vehicle.status].label}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {vehicle.name} • {vehicle.brand} {vehicle.model}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/vehicles/rental/${id}/edit`}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <Pencil className="h-4 w-4" />
            {t('common.edit')}
          </Link>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Truck className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('vehicle.basicInfo')}
              </h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('vehicle.licensePlate')}
                </p>
                <p className="font-medium text-gray-900 dark:text-white">{vehicle.licensePlate}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.type')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {TYPE_LABELS[vehicle.type]}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('service.designation')}
                </p>
                <p className="font-medium text-gray-900 dark:text-white">{vehicle.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('vehicle.brandModel')}
                </p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {vehicle.brand} {vehicle.model}
                </p>
              </div>
            </div>
            {vehicle.notes && (
              <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.notes')}</p>
                <p className="mt-1 text-gray-700 dark:text-gray-300">{vehicle.notes}</p>
              </div>
            )}
          </div>

          {/* Rental History */}
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
                <History className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('vehicle.rentalHistory')}
              </h2>
            </div>
            {historyLoading ? (
              <div className="py-4 text-center">
                <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('vehicle.loadingHistory')}
                </p>
              </div>
            ) : (rentalHistoryData?.rentals ?? []).length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">{t('vehicle.noRentalHistory')}</p>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {(rentalHistoryData?.rentals ?? []).map(rental => (
                  <div key={rental.rentalId} className="flex items-center justify-between py-3">
                    <div>
                      <Link
                        to={`/rental/${rental.rentalId}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {rental.rentalCode}
                      </Link>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {rental.partnerName}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {formatDate(rental.startDate)} -{' '}
                        {formatDate(rental.actualEnd ?? rental.expectedEnd)}
                      </p>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          rental.status === 'ACTIVE' || rental.status === 'active'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {rental.status === 'ACTIVE' || rental.status === 'active'
                          ? t('status.active')
                          : t('status.closed')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Expiry Dates Card */}
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-lg bg-orange-100 p-2 dark:bg-orange-900/30">
                <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('vehicle.expiryDates')}
              </h2>
            </div>
            <div className="space-y-3">
              <ExpiryBadge
                date={vehicle.technicalExpiryDate}
                label={t('vehicle.technicalInspection')}
              />
              <ExpiryBadge
                date={vehicle.registrationExpiryDate}
                label={t('vehicle.registrationCert')}
              />
              <ExpiryBadge date={vehicle.insuranceExpiryDate} label={t('vehicle.insurance')} />
            </div>
          </div>

          {/* Metadata Card */}
          <div className="kgc-card-bg rounded-lg border border-gray-200 p-6 dark:border-gray-700">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-lg bg-gray-100 p-2 dark:bg-gray-800">
                <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('vehicle.recordData')}
              </h2>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('common.createdAt')}</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {formatDateTime(vehicle.createdAt)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('vehicle.lastModified')}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {formatDateTime(vehicle.updatedAt)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
